/**
 * Counselor Authentication Context
 * Separate auth context for Counselor users
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCrmToken, setCrmToken, clearCrmToken, crmFetch } from '../utils/crmAuth';
import { isCrmEnabled } from '../utils/featureFlags';
import { API_BASE } from '../config/api';

const CounselorAuthContext = createContext(null);

export const useCounselorAuth = () => {
  const context = useContext(CounselorAuthContext);
  if (!context) {
    throw new Error('useCounselorAuth must be used within CounselorAuthProvider');
  }
  return context;
};

export const CounselorAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Bootstrap: Check if counselor is authenticated on mount
  useEffect(() => {
    if (!isCrmEnabled()) {
      setLoading(false);
      return;
    }

    const bootstrapAuth = async () => {
      const token = getCrmToken();
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Verify token by calling /me endpoint
        const response = await crmFetch('/api/crm/auth/me');
        
        if (response.ok) {
          const data = await response.json();
          // Verify it's a counselor
          if (data.role === 'counselor') {
            setUser(data);
            // Fetch profile if needed
            await fetchProfile();
          } else {
            // Not a counselor - clear token
            clearCrmToken();
            setUser(null);
          }
        } else if (response.status === 401) {
          // Only clear token on 401 (unauthorized) - not on network errors
          clearCrmToken();
          setUser(null);
          setProfile(null);
        } else {
          // Other errors (network, 500, etc.) - keep token, might be temporary
          console.warn('Counselor auth bootstrap failed with non-401 error:', response.status);
        }
      } catch (error) {
        // Network errors or other exceptions - don't clear token
        // Token might still be valid, just network issue
        console.warn('Error bootstrapping counselor auth (keeping token):', error);
        // Don't clear token on network errors - allow retry
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  const fetchProfile = async () => {
    if (!isCrmEnabled() || !user) {
      return;
    }

    try {
      setProfileLoading(true);
      const response = await crmFetch('/api/crm/profiles/me');
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else if (response.status === 404) {
        // Profile not found - this is expected for new users
        setProfile(null);
      } else {
        // Other error - clear profile
        setProfile(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE}/api/crm/counselor/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Verify user is counselor
      if (data.user.role !== 'counselor') {
        throw new Error('Counselor access required');
      }

      // Store token and user
      setCrmToken(data.access_token);
      setUser(data.user);
      
      // Fetch profile after login
      await fetchProfile();

      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional - stateless JWT)
      await crmFetch('/api/crm/auth/logout', {
        method: 'POST'
      });
    } catch (error) {
      // Ignore errors - we'll clear token anyway
    } finally {
      clearCrmToken();
      setUser(null);
      setProfile(null);
      navigate('/crm/counselor/login');
    }
  };

  const value = {
    user,
    profile,
    profileLoading,
    loading,
    login,
    logout,
    fetchProfile,
    isAuthenticated: !!user,
    hasProfile: !!profile,
  };

  return <CounselorAuthContext.Provider value={value}>{children}</CounselorAuthContext.Provider>;
};
